import { useForm } from 'react-hook-form';

import { useApplyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { Variant } from '@nucleus-commerce/api-client/admin';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { fromVariant, toVariantsUpdate } from '../api/variant-mappers';
import {
  variantFormSchema,
  type VariantFormInput,
  type VariantFormOutput,
} from '../schemas/variant-form-schema';

import { useUpdateVariant } from './use-variant-mutations';

export interface UseVariantFormOptions {
  productId: string;
  variant: Variant;
  /** After a save, for a host that shows something derived from the master variant. */
  onSaved?: (saved: Variant) => void;
}

/**
 * One master variant as a form under the save bar: Save sends only the changed fields, Discard
 * goes back to the saved values, a refused field lands on its input. Both variant pages use it,
 * so a host keys the component that calls this by variant.
 */
export function useVariantForm({ productId, variant, onSaved }: UseVariantFormOptions) {
  const message = useErrorMessage();
  const { can } = usePermissions();
  const canWrite = can('products.write');
  const update = useUpdateVariant(productId);

  const form = useForm<VariantFormInput, unknown, VariantFormOutput>({
    resolver: useI18nResolver(variantFormSchema),
    values: fromVariant(variant),
    resetOptions: { keepDirtyValues: true },
    disabled: !canWrite,
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);
  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    const [item] = toVariantsUpdate(
      { [variant.id]: values },
      { [variant.id]: form.formState.dirtyFields },
      [variant.id],
    );
    if (!item) return;
    const { id: _id, ...body } = item;
    try {
      const saved = await update.mutateAsync({ variantId: variant.id, body });
      form.reset(fromVariant(saved), { keepDirtyValues: false });
      onSaved?.(saved);
    } catch (error) {
      if (!applyFieldErrors(error)) {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    }
  });
  // `resetOptions` is for a refetch arriving mid-edit; a bare `form.reset()` would inherit it too
  // and leave every edit in place.
  useSaveBar({
    form,
    onSave: submit,
    onDiscard: () => form.reset(undefined, { keepDirtyValues: false }),
    saving: update.isPending,
    disabled: !canWrite,
  });

  return { form, submit };
}
